/**
 * Universal Protected Route - Handles OAuth, Demo, and VH Staff authentication
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Cookies from 'js-cookie';
import { useAuth } from '@/services/auth/authContext';
import { getUserSubscriptionStatus } from '@/utils/jwt';

interface UniversalProtectedRouteProps {
  children: React.ReactNode;
}

const UniversalProtectedRoute: React.FC<UniversalProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated: oauthAuthenticated, isLoading: oauthLoading } = useAuth();
  const [isDemoAuthenticated, setIsDemoAuthenticated] = useState(false);
  const [isDemoLoading, setIsDemoLoading] = useState(true);
  const [isVHStaffAuthenticated, setIsVHStaffAuthenticated] = useState(false);
  const [isVHStaffLoading, setIsVHStaffLoading] = useState(true);
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const location = useLocation();

  const isDemo = localStorage.getItem('isDemo') === 'true';
  const isVHStaff = localStorage.getItem('user_type') === 'vh_staff';

  useEffect(() => {
    console.log('[UniversalProtectedRoute] Initial state:', {
      isDemo: localStorage.getItem('isDemo'),
      isVHStaff: localStorage.getItem('user_type') === 'vh_staff',
      hasCookie: !!Cookies.get('auth_token'),
      sessionToken: !!sessionStorage.getItem('demo_token'),
      pathname: location.pathname
    });

    // VH Staff takes priority over Demo (more specific user type)
    // This prevents stale isDemo flags from redirecting VH Staff to /dancing-queen
    if (isVHStaff) {
      checkVHStaffAuth();
      setIsDemoLoading(false);
    } else if (isDemo) {
      checkDemoAuth();
      setIsVHStaffLoading(false);
    } else {
      setIsDemoLoading(false);
      setIsVHStaffLoading(false);
      // Check subscription status for OAuth users
      const token = Cookies.get('auth_token');
      if (token && oauthAuthenticated) {
        const status = getUserSubscriptionStatus(token);
        console.log('[UniversalProtectedRoute] Subscription status:', status);
        setSubscriptionRequired(status.subscriptionRequired || false);
      }
    }
  }, [isDemo, isVHStaff, oauthAuthenticated]);

  const checkDemoAuth = async () => {
    console.log('[UniversalProtectedRoute] Checking demo auth...');
    try {
      // For demo users, we validate authentication via API call
      // This is more secure than reading cookies directly
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://dineai.ai/api'}/demo/v3/validate`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'  // This sends httpOnly cookies automatically
      });

      console.log('[UniversalProtectedRoute] Demo validation response:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('[UniversalProtectedRoute] Demo user validated:', data.user?.email);
        setIsDemoAuthenticated(true);
      } else {
        console.log('[UniversalProtectedRoute] Demo validation failed:', response.status);
        setIsDemoAuthenticated(false);
        if (response.status === 401) {
          // Clear local demo flags on 401
          localStorage.removeItem('isDemo');
          localStorage.removeItem('demoPlan');
        }
      }
    } catch (error) {
      console.error('[UniversalProtectedRoute] Demo auth check error:', error);
      // On network error, assume not authenticated
      setIsDemoAuthenticated(false);
    } finally {
      setIsDemoLoading(false);
    }
  };

  const checkVHStaffAuth = async () => {
    console.log('[UniversalProtectedRoute] Checking VH Staff auth...');
    try {
      // For VH Staff, validate authentication via /me endpoint
      const response = await fetch('/api/vh-staff/auth/me', {
        method: 'GET',
        credentials: 'include'  // This sends httpOnly cookies automatically
      });

      console.log('[UniversalProtectedRoute] VH Staff validation response:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('[UniversalProtectedRoute] VH Staff validated:', data.email);
        setIsVHStaffAuthenticated(true);
      } else {
        console.log('[UniversalProtectedRoute] VH Staff validation failed:', response.status);
        setIsVHStaffAuthenticated(false);
        if (response.status === 401) {
          // Clear VH Staff localStorage on 401
          localStorage.removeItem('user_type');
          localStorage.removeItem('staff_name');
          localStorage.removeItem('restaurant_name');
          localStorage.removeItem('restaurant_slug');
          localStorage.removeItem('restaurant_logo_url');
          localStorage.removeItem('vh_staff_data');
        }
      }
    } catch (error) {
      console.error('[UniversalProtectedRoute] VH Staff auth check error:', error);
      // On network error, assume not authenticated
      setIsVHStaffAuthenticated(false);
    } finally {
      setIsVHStaffLoading(false);
    }
  };

  // Show loading spinner while checking auth
  // VH Staff checked first, then Demo, then OAuth
  const isLoading = isVHStaff ? isVHStaffLoading : (isDemo ? isDemoLoading : oauthLoading);
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-white border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
          <p className="mt-4 text-white text-lg">Loading...</p>
        </motion.div>
      </div>
    );
  }

  // Check authentication based on user type
  // VH Staff checked first, then Demo, then OAuth
  const isAuthenticated = isVHStaff
    ? isVHStaffAuthenticated
    : (isDemo ? isDemoAuthenticated : oauthAuthenticated);

  if (!isAuthenticated) {
    // Redirect based on user type (VH Staff first, then Demo, then OAuth)
    let redirectTo = '/restaurant-login';
    if (isVHStaff) {
      const slug = localStorage.getItem('restaurant_slug');
      redirectTo = slug ? `/staff/${slug}` : '/vh-staff-login';
    } else if (isDemo) {
      redirectTo = '/dancing-queen';
    }
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check subscription requirement for OAuth users (not demo, not VH Staff)
  // Skip this check if already on plan-selection page to avoid redirect loop
  if (!isDemo && !isVHStaff && subscriptionRequired && !location.pathname.includes('plan-selection')) {
    console.log('[UniversalProtectedRoute] Subscription required, redirecting to plan-selection');
    return <Navigate to="/plan-selection?required=true" replace />;
  }

  // Render protected content
  return <>{children}</>;
};

export default UniversalProtectedRoute;
